import {
  Center,
  Group,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconPlus } from "@tabler/icons-react"
import { Button, ActionIcon, SectionHeader, Loader } from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { ContactResponses } from "@tfg/types/endpoints/contacts"

import { DeleteContactModal } from "~/pages/SupplierPortal/Contacts/DeleteContactModal.tsx"
import { useListSupplierContactsQuery } from "~/pages/SupplierPortal/Contacts/hooks/useListSupplierContactQuery.ts"
import { ContactDrawer } from "~/pages/SupplierPortal/TraceabilityRequests/components/ContactDrawer.tsx"
import { useAuth } from "~/store/auth-context.tsx"

export const SupplierContacts = () => {
  const { t } = useTranslation(["supplier"])
  const { isAdmin } = useAuth()

  const { data: contacts, isLoading, isError } = useListSupplierContactsQuery()
  const [modalOpened, setModalOpened] = useState(false)
  const [openedDelete, { open: openDelete, close: closeDelete }] =
    useDisclosure()
  const [editedContact, setEditedContact] = useState<
    ContactResponses.Contact | undefined
  >()
  const [deletedContact, setDeletedContact] =
    useState<ContactResponses.Contact>()

  if (isLoading) {
    return <Loader fullPage />
  } else if (isError) {
    return (
      <Center h="100%">
        <Text c="red" fz="xl">
          {t("request.errors.generic")}
        </Text>
      </Center>
    )
  }
  return (
    <>
      <SectionHeader
        mainCta={
          <Button icon={<IconPlus />} onClick={() => setModalOpened(true)}>
            {t("contacts.buttons.add")}
          </Button>
        }
      />
      <Table verticalSpacing="sm" highlightOnHover>
        <TableThead>
          <TableTr>
            {isAdmin ? (
              <TableTh>
                <Text c="violet">{t("contacts.table.headers.id")}</Text>
              </TableTh>
            ) : null}
            <TableTh>{t("contacts.table.headers.name")}</TableTh>
            <TableTh>{t("contacts.table.headers.email")}</TableTh>
            <TableTh>{t("contacts.table.headers.actions")}</TableTh>
          </TableTr>
        </TableThead>
        <TableTbody>
          {contacts?.map((contact) => (
            <TableTr key={contact.id}>
              {isAdmin ? (
                <TableTd>
                  <Text c="violet">{contact.id}</Text>
                </TableTd>
              ) : null}
              <TableTd>{contact.user.name}</TableTd>
              <TableTd>{contact.user.email}</TableTd>
              <TableTd style={{ minWidth: "240px" }}>
                <Group wrap="nowrap">
                  <ActionIcon
                    type="edit"
                    onClick={() => {
                      setEditedContact(contact)
                      setModalOpened(true)
                    }}
                  />
                  <ActionIcon
                    type="delete"
                    onClick={() => {
                      setDeletedContact(contact)
                      openDelete()
                    }}
                  />
                </Group>
              </TableTd>
            </TableTr>
          ))}
        </TableTbody>
      </Table>
      {editedContact && (
        <ContactDrawer
          opened={modalOpened}
          contact={editedContact}
          action="edit"
          onClose={() => {
            setEditedContact(undefined)
            setModalOpened(false)
          }}
          onContactCreated={() => setModalOpened(false)}
          contactInitialValues={{
            name: editedContact.user.name,
            email: editedContact.user.email,
            preferredLanguage: editedContact.user.preferedLangageIso6391,
            companyName: editedContact.user.company.name,
            companyStreet: editedContact.user.company.street,
            companyCity: editedContact.user.company.city,
            companyPostalCode: editedContact.user.company.postalCode,
            companyCountry: editedContact.user.company.country,
          }}
        />
      )}
      <ContactDrawer
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        onContactCreated={() => setModalOpened(false)}
      />
      {deletedContact && (
        <DeleteContactModal
          isOpen={openedDelete}
          onClose={() => closeDelete()}
          contact={deletedContact}
        />
      )}
    </>
  )
}
